// File: src/components/OmniCine/Markers/MarkerOverlay.tsx
//
// MarkerOverlay — narrative / structural markers only
// A3N1 semantic rule:
//   - npc:* markers DO NOT render here
//   - NPC telemetry belongs exclusively to the NPC lane
//
// Visual differentiation:
//   - Narrative markers = diamond (rotated square)
//   - NPC markers = circular dots (NPC lane only)
//
// Interactions:
//   - Click marker → jump playhead
//   - Drag marker → move marker
//   - Deterministic click-vs-drag via pixel threshold
//   - Double-click marker → request marker edit
//   - Context menu / long-press marker → request marker actions
//
// SAFE:
//   - snap optional (defaults to non-snapping)
//   - viewport-relative (timeline-space)
//   - dispatcher-only (no ownership)
//   - no reliance on snap.enabled existence

import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  emitMarkerContextRequest as emitTimelineMarkerContextRequest,
  emitMarkerEditRequest as emitTimelineMarkerEditRequest,
} from "@/contracts/omnicine/TimelineSurfaceEvents";

import AnnotationMarkerRenderer from "../Annotations/AnnotationMarkerRenderer";
import { buildAnnotationMarkerReadModel } from "../Annotations/buildAnnotationMarkerReadModel";

import type {
  TimelineAction,
  TimelineMarker,
  TimelineSnap,
} from "@/state/useOmniCineTimeline";

/* ------------------------------------------------------------------ */
/* Snap defaults                                                       */
/* ------------------------------------------------------------------ */

const FALLBACK_SNAP: TimelineSnap = {
  enabled: false,
  mode: "second",
  fps: 30,
};

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const CLICK_DRAG_THRESHOLD_PX = 4;
const MARKER_DOUBLE_CLICK_MS = 280;
const MARKER_LONG_PRESS_MS = 520;
const MARKER_LONG_PRESS_CANCEL_PX = 6;

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function nowMs() {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

/**
 * A3N1 semantic guard:
 * NPC markers (npc:*) never render in MarkerOverlay.
 */
function isNpcMarkerLabel(label?: string) {
  const s = (label ?? "").toString().trim().toLowerCase();
  return s.startsWith("npc:");
}

function normalizeSnap(snap?: TimelineSnap): TimelineSnap {
  if (!snap) return FALLBACK_SNAP;
  return {
    enabled: !!snap.enabled,
    mode: snap.mode ?? FALLBACK_SNAP.mode,
    fps: Number.isFinite(snap.fps) ? snap.fps : FALLBACK_SNAP.fps,
  };
}

function snapTimeSec(t: number, snap: TimelineSnap) {
  if (!snap.enabled) return t;

  const fps = Number.isFinite(snap.fps) ? snap.fps : 30;
  const mode = snap.mode ?? "second";

  if (mode === "second") return Math.round(t);

  if (mode === "frame") {
    const step = 1 / Math.max(1, fps);
    return Math.round(t / step) * step;
  }

  return t;
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function MarkerOverlay({
  markers,
  pxPerSec,
  viewportStartSec,
  viewportEndSec,
  durationSec,
  dispatch,
  snap,
  activeMarkerId,
  onActiveMarkerChange,
}: {
  markers: TimelineMarker[];
  pxPerSec: number;
  viewportStartSec: number;
  viewportEndSec: number;
  durationSec: number;
  dispatch: (action: TimelineAction) => void;
  snap?: TimelineSnap;
  activeMarkerId?: string | null;
  onActiveMarkerChange?: (markerId: string, source: string) => void;
}): JSX.Element {
  const layerRef = useRef<HTMLDivElement | null>(null);

  const [dragId, setDragId] = useState<string | null>(null);
  const [hoveredAnnotationMarkerId, setHoveredAnnotationMarkerId] = useState<string | null>(null);

  /* ------------------------------------------------------------------ */
  /* Pointer state                                                      */
  /* ------------------------------------------------------------------ */

  const dragRef = useRef<{ id: string } | null>(null);

  const pointerRef = useRef<{
    id: string;
    x0: number;
    y0: number;
    moved: boolean;
  } | null>(null);

  const lastClickRef = useRef<{ id: string; ts: number } | null>(null);

  const longPressRef = useRef<{
    id: string;
    pointerId: number;
    x0: number;
    y0: number;
    timer: number | null;
    fired: boolean;
  } | null>(null);

  const markerById = useMemo(() => {
    const m = new Map<string, TimelineMarker>();
    for (const mk of markers ?? []) m.set(String(mk.id), mk);
    return m;
  }, [markers]);

  const emitMarkerEditRequest = useCallback(
    (id: string, source: string) => {
      const marker = markerById.get(id);
      emitTimelineMarkerEditRequest({
        markerId: id,
        timeSec: Number(marker?.timeSec ?? 0),
        label: marker?.label ?? null,
        type: (marker as any)?.type ?? null,
        source,
      });
    },
    [markerById],
  );

  const emitMarkerContextRequest = useCallback(
    (id: string, source: string, clientX: number, clientY: number) => {
      const marker = markerById.get(id);
      emitTimelineMarkerContextRequest({
        markerId: id,
        timeSec: Number(marker?.timeSec ?? 0),
        label: marker?.label ?? null,
        type: (marker as any)?.type ?? null,
        clientX,
        clientY,
        source,
      });
    },
    [markerById],
  );

  const clearLongPress = useCallback(() => {
    const lp = longPressRef.current;
    if (!lp) return;
    if (lp.timer != null) {
      window.clearTimeout(lp.timer);
      lp.timer = null;
    }
  }, []);

  const snapCfg = useMemo(() => normalizeSnap(snap), [snap]);

  /* ------------------------------------------------------------------ */
  /* Visible markers (A3N1-filtered)                                    */
  /* ------------------------------------------------------------------ */

  const visibleMarkers = useMemo(() => {
    return (markers ?? [])
      .filter((m) => !isNpcMarkerLabel((m as any).label))
      .filter(
        (m) =>
          Number.isFinite(m.timeSec) &&
          m.timeSec >= viewportStartSec &&
          m.timeSec <= viewportEndSec,
      )
      .slice()
      .sort((a, b) => a.timeSec - b.timeSec);
  }, [markers, viewportStartSec, viewportEndSec]);

  const annotationMarkersById = useMemo(() => {
    const next = new Map<string, ReturnType<typeof buildAnnotationMarkerReadModel>>();
    for (const marker of visibleMarkers) {
      const readModel = buildAnnotationMarkerReadModel({
        marker,
        pxPerSec,
        viewportStartSec,
        activeMarkerId,
      });
      if (readModel) next.set(readModel.markerId, readModel);
    }
    return next;
  }, [activeMarkerId, pxPerSec, viewportStartSec, visibleMarkers]);

  /* ------------------------------------------------------------------ */
  /* Coordinate conversion                                              */
  /* ------------------------------------------------------------------ */

  const clientXToTime = useCallback(
    (clientX: number) => {
      const el = layerRef.current;
      if (!el) {
        return snapTimeSec(
          clamp(viewportStartSec, 0, Math.max(0, durationSec)),
          snapCfg,
        );
      }

      const rect = el.getBoundingClientRect();
      const localX = clientX - rect.left;

      const t =
        viewportStartSec +
        localX / Math.max(1e-6, pxPerSec);

      return snapTimeSec(
        clamp(t, 0, Math.max(0, durationSec)),
        snapCfg,
      );
    },
    [viewportStartSec, pxPerSec, durationSec, snapCfg],
  );

  /* ------------------------------------------------------------------ */
  /* Pointer handlers                                                   */
  /* ------------------------------------------------------------------ */

  const beginDrag = useCallback((e: React.PointerEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      // Synthetic PointerEvents in console smoke scripts may not own an active pointer.
      // Continue without capture so scripted gesture tests can still exercise logic.
    }

    dragRef.current = { id };
    setDragId(id);
    onActiveMarkerChange?.(id, "marker:pointer-down");

    pointerRef.current = {
      id,
      x0: e.clientX,
      y0: e.clientY,
      moved: false,
    };

    clearLongPress();

    if (e.pointerType === "touch") {
      const timer = window.setTimeout(() => {
        const lp = longPressRef.current;
        if (!lp || lp.id !== id || lp.pointerId !== e.pointerId) return;
        lp.fired = true;
        emitMarkerContextRequest(id, "marker/long-press", e.clientX, e.clientY);
      }, MARKER_LONG_PRESS_MS);

      longPressRef.current = {
        id,
        pointerId: e.pointerId,
        x0: e.clientX,
        y0: e.clientY,
        timer,
        fired: false,
      };
    }
  }, [clearLongPress, emitMarkerContextRequest, onActiveMarkerChange]);

  const onDragMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current) return;
      e.preventDefault();

      if (pointerRef.current) {
        const dx = Math.abs(e.clientX - pointerRef.current.x0);
        const dy = Math.abs(e.clientY - pointerRef.current.y0);
        if (dx > CLICK_DRAG_THRESHOLD_PX || dy > CLICK_DRAG_THRESHOLD_PX) {
          pointerRef.current.moved = true;
        }

        if (dx > MARKER_LONG_PRESS_CANCEL_PX || dy > MARKER_LONG_PRESS_CANCEL_PX) {
          clearLongPress();
        }
      }

      const t = clientXToTime(e.clientX);

      dispatch({
        type: "marker/move" as any,
        payload: { markerId: dragRef.current.id, timeSec: t },
        meta: { source: "user", transient: true },
      });
    },
    [clientXToTime, dispatch, clearLongPress],
  );

  const endDrag = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current) return;
      e.preventDefault();

      const t = clientXToTime(e.clientX);
      const id = dragRef.current.id;

      const wasClick =
        !!pointerRef.current && !pointerRef.current.moved;

      if (wasClick) {
        const lp = longPressRef.current;
        const longPressFired = !!lp?.fired;

        if (longPressFired) {
          // Long-press opens context actions and suppresses click/double-click behavior.
          lastClickRef.current = null;
        } else {
          const now = nowMs();
          const prev = lastClickRef.current;
          const isDoubleClick = !!prev && prev.id === id && now - prev.ts <= MARKER_DOUBLE_CLICK_MS;

          if (isDoubleClick) {
            onActiveMarkerChange?.(id, "marker/double-click");
            emitMarkerEditRequest(id, "marker/double-click");
            lastClickRef.current = null;
          } else {
            onActiveMarkerChange?.(id, "marker/click");
            dispatch({
              type: "timeline/setPlayhead" as any,
              payload: { timeSec: t },
              meta: { source: "user" },
            });
            lastClickRef.current = { id, ts: now };
          }
        }
      } else {
        onActiveMarkerChange?.(id, "marker/drag");
        dispatch({
          type: "marker/move" as any,
          payload: { markerId: id, timeSec: t },
          meta: { source: "user" },
        });
        lastClickRef.current = null;
      }

      clearLongPress();
      dragRef.current = null;
      longPressRef.current = null;
      pointerRef.current = null;
      setDragId(null);
    },
    [clientXToTime, dispatch, emitMarkerEditRequest, clearLongPress, onActiveMarkerChange],
  );

  const onMarkerContextMenu = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.preventDefault();
      e.stopPropagation();
      clearLongPress();
      onActiveMarkerChange?.(id, "marker/context-menu");
      emitMarkerContextRequest(id, "marker/context-menu", e.clientX, e.clientY);
    },
    [clearLongPress, emitMarkerContextRequest, onActiveMarkerChange],
  );

  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */

  return (
    <div
      ref={layerRef}
      className="pointer-events-none absolute inset-0 z-20"
    >
      {visibleMarkers.map((m) => {
        const left = (m.timeSec - viewportStartSec) * pxPerSec;
        const markerId = String(m.id ?? "");
        const isActive = markerId && String(activeMarkerId ?? "") === markerId;
        const annotationMarker = annotationMarkersById.get(markerId) ?? null;
        const isHoveredAnnotation = hoveredAnnotationMarkerId === markerId;

        return (
          <div
            key={m.id}
            className="absolute top-0 h-full"
            style={{ left, transform: "translateX(-50%)" }}
          >
            {/* Stem */}
            <div
              className="absolute inset-y-0"
              style={{
                width: 1,
                background:
                  "linear-gradient(to bottom, rgba(255,255,255,0.10), rgba(255,255,255,0.14), rgba(255,255,255,0.10))",
                boxShadow: "0 0 10px rgba(34,211,238,0.08)",
              }}
            />

            {/* Narrative marker handle (DIAMOND) */}
            <button
              type="button"
              data-oc-marker-id={markerId}
              data-oc-marker-time={Number(m.timeSec).toFixed(3)}
              data-oc-marker-annotation={annotationMarker ? "1" : "0"}
              data-oc-marker-annotation-type={annotationMarker?.annotationType ?? ""}
              className={[
                "pointer-events-auto absolute top-2",
                "h-4 w-4 -translate-x-1/2 rotate-45 rounded-[4px]",
                isActive
                  ? "border border-cyan-100/85 bg-cyan-300/35"
                  : "border border-white/20 bg-white/[0.10]",
                "backdrop-blur shadow",
                "transition-transform",
                dragId === m.id ? "scale-110" : "hover:scale-110",
              ].join(" ")}
              aria-label={`Marker at ${m.timeSec.toFixed(2)}s`}
              onPointerDown={(e) => beginDrag(e, m.id)}
              onPointerMove={onDragMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onPointerEnter={() => setHoveredAnnotationMarkerId(annotationMarker ? markerId : null)}
              onPointerLeave={() => setHoveredAnnotationMarkerId((current) => (current === markerId ? null : current))}
              onContextMenu={(e) => onMarkerContextMenu(e, m.id)}
              title="" // prevent OS tooltip
            >
              {annotationMarker ? (
                <AnnotationMarkerRenderer marker={annotationMarker} hovered={isHoveredAnnotation} />
              ) : (
                <span
                  aria-hidden
                  className="absolute inset-1 rounded-full -rotate-45"
                  style={{
                    background: isActive ? "rgba(207,250,254,0.98)" : "rgba(34,211,238,0.92)",
                    boxShadow: isActive
                      ? "0 0 16px rgba(34,211,238,0.78)"
                      : "0 0 10px rgba(34,211,238,0.45)",
                  }}
                />
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
}
